import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { RouterInputs } from '@polatkan/api';
import { ArrowLeft, Loader2, Plus, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { usePermission } from '@/components/guards/permission-guard';
import {
  FinancialCorrectionDialog,
  type FinancialReview,
} from '@/components/trading/sales/financial-correction-dialog';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

type Change = RouterInputs['tradingInvoice']['previewEdit'];
type InvoiceForm = {
  billingAddress: string;
  billingCity: string;
  billingState: string;
  billingPostal: string;
  billingCountry: string;
  taxId: string;
  dueDate: string;
  paymentTerms: string;
  notes: string;
  internalNotes: string;
  items: NonNullable<Change['items']>;
};

export function TradingInvoiceEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('trading');
  const { t: common } = useTranslation('common');
  const utils = trpc.useUtils();
  const canEdit = usePermission('INVOICES', 'EDIT', 'TRADING');
  const canEditOrder = usePermission('SALES_ORDERS', 'EDIT', 'TRADING');
  const canViewOrder = usePermission('SALES_ORDERS', 'VIEW', 'TRADING');
  const {
    data: invoice,
    isLoading,
    error,
  } = trpc.tradingInvoice.byId.useQuery(id!, { enabled: !!id });
  const [initialized, setInitialized] = useState<string>();
  const [financial, setFinancial] = useState(false);
  const [review, setReview] = useState<FinancialReview | null>(null);
  const [change, setChange] = useState<Change | null>(null);
  const [previewPending, setPreviewPending] = useState(false);
  const { register, handleSubmit, control, reset } = useForm<InvoiceForm>({
    defaultValues: { items: [] },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'items' });
  useEffect(() => {
    if (!invoice || initialized === invoice.id) return;
    reset({
      billingAddress: invoice.billingAddress,
      billingCity: invoice.billingCity,
      billingState: invoice.billingState ?? '',
      billingPostal: invoice.billingPostal ?? '',
      billingCountry: invoice.billingCountry,
      taxId: invoice.taxId ?? '',
      dueDate: format(new Date(invoice.dueDate), 'yyyy-MM-dd'),
      paymentTerms: invoice.paymentTerms ?? '',
      notes: invoice.notes ?? '',
      internalNotes: invoice.internalNotes ?? '',
      items: invoice.items.map((item) => ({
        productId: item.productId ?? undefined,
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        discountPercent: item.discountPercent,
        taxRate: item.taxRate,
      })),
    });
    setInitialized(invoice.id);
  }, [invoice, initialized, reset]);
  const save = trpc.tradingInvoice.saveEdit.useMutation({
    onSuccess: async () => {
      await utils.invalidate();
      showSuccessToast(t('common.saved'));
      navigate(`/trading/finance/invoices/${id}`);
    },
    onError: (error) => showErrorToast(error.message),
  });
  const onSubmit = async (form: InvoiceForm) => {
    if (!id || !canEdit || previewPending || save.isPending) return;
    const prepared: Change = {
      id,
      data: {
        billingAddress: form.billingAddress,
        billingCity: form.billingCity,
        billingState: form.billingState || null,
        billingPostal: form.billingPostal || null,
        billingCountry: form.billingCountry,
        taxId: form.taxId || null,
        ...(invoice && form.dueDate !== format(new Date(invoice.dueDate), 'yyyy-MM-dd')
          ? { dueDate: new Date(form.dueDate + 'T12:00:00Z') }
          : {}),
        paymentTerms: form.paymentTerms || null,
        notes: form.notes || null,
        internalNotes: form.internalNotes || null,
      },
      ...(financial ? { items: form.items } : {}),
    };
    try {
      setPreviewPending(true);
      const result = await utils.tradingInvoice.previewEdit.fetch(prepared, { staleTime: 0 });
      setChange(prepared);
      setReview(result);
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : common('error'));
    } finally {
      setPreviewPending(false);
    }
  };
  const confirm = async (correctionReason: string) => {
    if (!change || !review || save.isPending) return;
    try {
      await save.mutateAsync({ ...change, reviewToken: review.token, correctionReason });
    } catch {
      setReview(null);
      setChange(null);
    }
  };
  const busy = previewPending || save.isPending;
  if (isLoading)
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  if (error || !invoice)
    return (
      <div className="space-y-4">
        <Link to="/trading/finance/invoices">{common('back')}</Link>
        <p role="alert">{error?.message || t('finance.invoices.notFound')}</p>
      </div>
    );
  if (!canEdit) return <p role="alert">{t('sales.correction.noInvoicePermission')}</p>;
  return (
    <div className="space-y-6">
      <FinancialCorrectionDialog
        review={review}
        pending={save.isPending}
        onClose={() => {
          setReview(null);
          setChange(null);
        }}
        onConfirm={(reason) => void confirm(reason)}
        confirmLabel={t('finance.invoices.saveInvoiceChanges')}
      />
      <div className="flex items-center justify-between gap-3">
        <div>
          <Link
            to={`/trading/finance/invoices/${id}`}
            className="text-muted-foreground inline-flex items-center gap-2 text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            {common('back')}
          </Link>
          <h1 className="mt-2 text-2xl font-bold">{t('finance.invoices.editInvoice')}</h1>
          <p className="text-muted-foreground font-mono">{invoice.invoiceNumber}</p>
        </div>
        <Button disabled={busy} onClick={handleSubmit(onSubmit)}>
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t('sales.correction.review')}
        </Button>
      </div>
      {invoice.salesOrderId && (
        <section className="bg-muted/40 space-y-3 rounded border p-4">
          <p>{t('finance.invoices.sharedFinancialEditor')}</p>
          <Button
            variant="outline"
            disabled={!canEditOrder || !canViewOrder}
            onClick={() =>
              navigate(
                `/trading/sales/orders/${invoice.salesOrderId}/edit?fromInvoice=${encodeURIComponent(invoice.id)}`
              )
            }
          >
            {t('finance.invoices.editOrderAndInvoice')}
          </Button>
          {(!canEditOrder || !canViewOrder) && (
            <p className="text-muted-foreground text-sm">
              {t('sales.correction.noOrderPermission')}
            </p>
          )}
        </section>
      )}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <fieldset disabled={busy} className="bg-card grid gap-4 rounded border p-6 sm:grid-cols-2">
          <legend className="px-2 font-semibold">{t('finance.invoices.invoiceDetails')}</legend>
          {(
            [
              'billingAddress',
              'billingCity',
              'billingState',
              'billingPostal',
              'billingCountry',
              'taxId',
              'dueDate',
              'paymentTerms',
            ] as const
          ).map((field) => (
            <div key={field} className="space-y-2">
              <Label htmlFor={field}>{t(`finance.invoices.${field}`)}</Label>
              <Input
                id={field}
                type={field === 'dueDate' ? 'date' : 'text'}
                {...register(field, { required: field === 'dueDate' })}
              />
            </div>
          ))}
          {(['notes', 'internalNotes'] as const).map((field) => (
            <div key={field} className="space-y-2">
              <Label htmlFor={field}>{t(`finance.invoices.${field}`)}</Label>
              <Textarea id={field} {...register(field)} />
            </div>
          ))}
        </fieldset>
        {!invoice.salesOrderId && invoice.status === 'DRAFT' && (
          <section className="bg-card space-y-4 rounded border p-6">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={financial}
                onChange={(event) => setFinancial(event.target.checked)}
                disabled={busy}
              />
              {t('finance.invoices.editAmounts')}
            </label>
            {financial && (
              <fieldset disabled={busy} className="space-y-4">
                {fields.map((item, index) => (
                  <div key={item.id} className="grid gap-3 rounded border p-3 sm:grid-cols-6">
                    <div className="sm:col-span-2">
                      <Label htmlFor={`description-${index}`}>
                        {t('sales.orders.description')}
                      </Label>
                      <Input
                        id={`description-${index}`}
                        {...register(`items.${index}.description`, { required: true })}
                      />
                    </div>
                    {(['quantity', 'unitPrice', 'discountPercent', 'taxRate'] as const).map(
                      (field) => (
                        <div key={field}>
                          <Label htmlFor={`${field}-${index}`}>{t(`sales.orders.${field}`)}</Label>
                          <Input
                            id={`${field}-${index}`}
                            type="number"
                            min={field === 'quantity' ? 1 : 0}
                            max={
                              field === 'discountPercent' || field === 'taxRate' ? 100 : undefined
                            }
                            step={field === 'quantity' ? 1 : 0.01}
                            {...register(`items.${index}.${field}`, {
                              valueAsNumber: true,
                              required: true,
                            })}
                          />
                        </div>
                      )
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      aria-label={common('delete')}
                      onClick={() => remove(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    append({
                      description: '',
                      quantity: 1,
                      unitPrice: 0,
                      discountPercent: 0,
                      taxRate: 0,
                    })
                  }
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t('finance.invoices.addLine')}
                </Button>
              </fieldset>
            )}
          </section>
        )}
        {!invoice.salesOrderId && invoice.status !== 'DRAFT' && (
          <p className="text-muted-foreground text-sm">
            {t('finance.invoices.standaloneCorrection')}{' '}
            <Link className="underline" to="/trading/finance/credit-notes">
              {t('finance.creditNotes.title')}
            </Link>
          </p>
        )}
        <Button disabled={busy} type="submit">
          {t('sales.correction.review')}
        </Button>
      </form>
    </div>
  );
}
